import rest from "@/rest-client";
import createCrudVuexModule from "@/store/crud";

export const firmAudit = createCrudVuexModule({
  persistent: ["selectedEntity"],
  namespaced: true,
  path: "/firm-audit",
  state: {
    summaryPagination: undefined,
    isSummaryLoading: false,
    detailPagination: undefined,
    isDetailLoading: false,
    selectedFirm: undefined,
    audits: undefined,
    selectedAudit: undefined,
    isAuditsLoading: false,
    overview: undefined,
    isOverviewLoading: false,
    firmDashboardDetail: undefined,
    isFirmDashboardDetailLoading: false,
  },
  getters: {
    summaryPagination: (state) => state.summaryPagination,
    isSummaryLoading: (state) => state.isSummaryLoading,
    detailPagination: (state) => state.detailPagination,
    isDetailLoading: (state) => state.isDetailLoading,
    selectedFirm: (state) => state.selectedFirm,
    audits: (state) => state.audits,
    selectedAudit: (state) => state.selectedAudit,
    isAuditsLoading: (state) => state.isAuditsLoading,
    overview: (state) => state.overview,
    isOverviewLoading: (state) => state.isOverviewLoading,
    firmDashboardDetail: (state) => state.firmDashboardDetail,
    isFirmDashboardDetailLoading: (state) => state.isFirmDashboardDetailLoading,
  },
  mutations: {
    summaryPagination: (state, value) => (state.summaryPagination = value),
    isSummaryLoading: (state, value) => (state.isSummaryLoading = value),
    detailPagination: (state, value) => (state.detailPagination = value),
    isDetailLoading: (state, value) => (state.isDetailLoading = value),
    selectedFirm: (state, value) => (state.selectedFirm = value),
    audits: (state, value) => (state.audits = value),
    selectedAudit: (state, value) => (state.selectedAudit = value),
    isAuditsLoading: (state, value) => (state.isAuditsLoading = value),
    overview: (state, value) => (state.overview = value),
    isOverviewLoading: (state, value) => (state.isOverviewLoading = value),
    firmDashboardDetail: (state, value) => (state.firmDashboardDetail = value),
    isFirmDashboardDetailLoading: (state, value) => (state.isFirmDashboardDetailLoading = value),
  },
  actions: {
    async fetchOverview({ commit }, payload = {}) {
      commit("isOverviewLoading", true);
      try {
        const response = await rest.post("/dashboard/fwsed/overview", payload);
        commit("overview", response.data);
        return response;
      } finally {
        commit("isOverviewLoading", false);
      }
    },
    async fetchAudits({ commit }, { firmId, pagination = {}, clearStateBeforeRest = false } = {}) {
      commit("isAuditsLoading", true);
      try {
        if (!firmId) {
          commit("audits", undefined);
          return;
        }
        if (clearStateBeforeRest) {
          commit("audits", undefined);
        }
        const response = await rest.post(`/firm/${firmId}/audit/paginate`, { pagination });
        commit("audits", response.data);
        return response;
      } finally {
        commit("isAuditsLoading", false);
      }
    },
    async createAudit({ dispatch, commit, state }, { firmId, name, pagination }) {
      const response = await rest.post(`/firm/${firmId}/audit`, { name });
      const paginate = pagination || { options: { page: 1, limit: 50, sort: { createdAt: "desc" } } };
      await dispatch("fetchAudits", { firmId, pagination: paginate });
      if (response?.data) {
        commit("selectedAudit", response.data);
      }
      return response;
    },
    async updateAudit({ dispatch, commit }, { firmId, auditId, name, status, description, startedAt, completedAt, pagination }) {
      const payload = { name, status, description, startedAt, completedAt };
      Object.keys(payload).forEach((key) => payload[key] === undefined && delete payload[key]);

      const response = await rest.put(`/firm/${firmId}/audit/${auditId}`, payload);
      const paginate = pagination || { options: { page: 1, limit: 50, sort: { createdAt: "desc" } } };
      await dispatch("fetchAudits", { firmId, pagination: paginate });
      if (response?.data) {
        commit("selectedAudit", response.data);
      }
      return response;
    },
    async deleteAudit({ dispatch, commit, state }, { firmId, auditId, pagination }) {
      const response = await rest.delete(`/firm/${firmId}/audit/${auditId}`);
      const paginate = pagination || { options: { page: 1, limit: 50, sort: { createdAt: "desc" } } };
      await dispatch("fetchAudits", { firmId, pagination: paginate });

      const remaining = state.audits?.docs ?? [];
      const nextSelected = remaining.length ? remaining[0] : undefined;
      commit("selectedAudit", nextSelected);
      return response;
    },
    async fetchSummary({ commit }, pagination) {
      commit("isSummaryLoading", true);
      try {
        const response = await rest.post("/question-set/firm/paginate", { pagination });
        commit("summaryPagination", response.data);
        return response;
      } finally {
        commit("isSummaryLoading", false);
      }
    },
    async fetchFirmQuestionSets({ commit, state }, { firmId, pagination = {}, auditId } = {}) {
      if (!firmId) {
        commit("detailPagination", undefined);
        commit("selectedFirm", undefined);
        commit("selectedAudit", undefined);
        return;
      }
      commit("isDetailLoading", true);
      try {
        const response = await rest.post(`/question-set/firm/${firmId}/audit/${auditId}/paginate`, { pagination });
        commit("detailPagination", response.data);
        commit("selectedFirm", response.data.firm);

        return response;
      } finally {
        commit("isDetailLoading", false);
      }
    },
    async fetchFirmDashboardDetail({ commit }, { firmId, auditId } = {}) {
      if (!firmId) {
        commit("firmDashboardDetail", undefined);
        return;
      }
      commit("isFirmDashboardDetailLoading", true);
      try {
        const payload = auditId ? { auditId } : {};
        const response = await rest.post(`/dashboard/fwsed/firm/${firmId}/detail`, payload);
        commit("firmDashboardDetail", response.data);
        return response;
      } finally {
        commit("isFirmDashboardDetailLoading", false);
      }
    },
  },
});
